import { Component, inject, OnInit, signal, ViewChildren, ViewEncapsulation, ChangeDetectionStrategy } from "@angular/core";
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatCardModule } from "@angular/material/card";
import { MatCalendar, MatDatepickerModule } from "@angular/material/datepicker";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { MatSelectModule } from "@angular/material/select";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { NextLocator, SelectOption } from "@grupo-constant/next-dtype/types";
import { TranslocoModule } from "@ngneat/transloco";
import { differenceInCalendarMonths, getDay, getYear, lastDayOfMonth, startOfToday } from "date-fns";
import { firstValueFrom, Subject, takeUntil } from "rxjs";
import { DialogsService } from "../dialogs.service";
import { PipesModule } from "../../core/pipes/pipes.module";
import { NextCalendar } from "../../modules/admin/projects/projects.types";
import { HttpService } from "../../core/http/http.service";
import { LocalizeService } from "../../core/pipes/localize.service";
import { ProjectsService } from "../../modules/admin/projects/projects.service";
import { NextMethodsEnum } from "../../core/enums/next-methods.enum";

@Component({
    selector: 'app-edit-calendar-dialog',
    templateUrl: './edit-calendar-dialog.component.html',
    styleUrls: ['./edit-calendar-dialog.component.scss'],
    imports: [FormsModule, PipesModule, MatButtonModule, MatCardModule, MatDatepickerModule, MatFormFieldModule, MatIconModule, MatInputModule, MatProgressBarModule, MatSelectModule, MatSlideToggleModule, ReactiveFormsModule, TranslocoModule],
    changeDetection: ChangeDetectionStrategy.Eager,
    encapsulation: ViewEncapsulation.None
})
export class EditCalendarDialogComponent implements OnInit {

    @ViewChildren(MatCalendar) calendarMatrix: MatCalendar<Date>[];

    formGroup = new FormGroup({
        _oid: new FormControl<string>(undefined),
        _deprecated: new FormControl<boolean>(false),
        name: new FormControl<string>('', [Validators.required, Validators.pattern(/^[a-zA-Z]{1}[a-zA-Z0-9 -_]+$/), Validators.maxLength(100)]),
        syncOid: new FormControl<string>(''),
        keepSync: new FormControl<boolean>(false),
        weekDays: new FormControl<string[]>([])
    });

    year = getYear(startOfToday());
    disableNextYear = false;

    months = signal<string[][]>([]);

    daysSelected: string[] = [];
    weekdaysOption: SelectOption[] = [];
    calendarGroups = new Map<string, NextCalendar[]>();
    readonly = false;
    loading = true;

    public data: { calendar: NextCalendar, calendarList: NextCalendar[], locator: NextLocator, readonly: boolean } = inject(MAT_DIALOG_DATA);
    public dialogRef = inject(MatDialogRef<EditCalendarDialogComponent>);
    private dialogsService = inject(DialogsService);
    private httpService = inject(HttpService);
    private localize = inject(LocalizeService);
    private projectsService = inject(ProjectsService);

    private readonly weekdaysClass = 'com.cloudcodice.feature.dtype.enumeration.WeekDays';
    private unsubscribeAll = new Subject<void>();

    async ngOnInit() {
        if (this.data.calendar) {
            this.formGroup.patchValue(this.data.calendar);
            if (this.data.calendar.syncOid) {
                this.formGroup.get('keepSync').setValue(true);
            }
            this.daysSelected = await firstValueFrom(this.projectsService.retrieveCalendarDateList({ calendarOid: this.data.calendar._oid }));
            if (!this.data.calendar.sameLocator || this.data.readonly) {
                this.formGroup.disable();
                this.readonly = true;
            }
        }
        this.httpService.post<{ option: SelectOption[] }>(NextMethodsEnum.retrieveEnumerationType, { enumerationClass: this.weekdaysClass })
            .subscribe((reply) => this.weekdaysOption = reply.option);

        for (const calendar of this.data.calendarList) {
            if (!this.calendarGroups.has(calendar.belongsToCaption)) {
                this.calendarGroups.set(calendar.belongsToCaption, []);
            }
            this.calendarGroups.get(calendar.belongsToCaption).push(calendar);
        }

        this.formGroup.get('syncOid').valueChanges
            .pipe(takeUntil(this.unsubscribeAll))
            .subscribe(async oid => {
                if (oid) {
                    this.loading = true;
                    const dates = await firstValueFrom(this.projectsService.retrieveCalendarDateList({ calendarOid: oid }));
                    for (const date of dates) {
                        if (!this.daysSelected.includes(date)) {
                            this.daysSelected.push(date);
                        }
                    }
                    for (const calendar of this.calendarMatrix) {
                        calendar.updateTodaysDate();
                    }
                    this.loading = false;
                }
            });
        this.formGroup.get('weekDays').valueChanges
            .pipe(takeUntil(this.unsubscribeAll))
            .subscribe(() => {
                for (const calendar of this.calendarMatrix) {
                    calendar.updateTodaysDate();
                }
            });

        this.months.set(this.getMonthsMatrix());
        this.loading = false;
    }

    dateFilter = (d: Date | null): boolean => {
        if (this.readonly) {
            return false;
        }
        if (differenceInCalendarMonths(new Date(), d) > 1) {
            return false;
        }
        const weekDays = this.formGroup.get('weekDays').value;
        if (weekDays.length > 0) {
            const day = getDay(d);
            switch (day) {
                case 1:
                    return !weekDays.includes('MONDAY');
                case 2:
                    return !weekDays.includes('TUESDAY');
                case 3:
                    return !weekDays.includes('WEDNESDAY');
                case 4:
                    return !weekDays.includes('THURSDAY');
                case 5:
                    return !weekDays.includes('FRIDAY');
                case 6:
                    return !weekDays.includes('SATURDAY');
                case 0:
                    return !weekDays.includes('SUNDAY');
            }
        }
        return true;
    }

    isSelected = (event: Date) => {
        const date =
            event.getFullYear() +
            "-" +
            ("00" + (event.getMonth() + 1)).slice(-2) +
            "-" +
            ("00" + event.getDate()).slice(-2);
        return this.daysSelected.find(x => x == date) ? 'selected' : null;
    };

    select(event: Date, calendar: MatCalendar<Date>) {
        const date =
            event.getFullYear() +
            "-" +
            ("00" + (event.getMonth() + 1)).slice(-2) +
            "-" +
            ("00" + event.getDate()).slice(-2);
        const index = this.daysSelected.findIndex(x => x == date);
        if (index < 0) this.daysSelected.push(date);
        else this.daysSelected.splice(index, 1);
        calendar.updateTodaysDate();
    }

    previousYear() {
        this.year = this.year - 1;
        this.months.set(this.getMonthsMatrix());
        this.disableNextYear = this.year - getYear(new Date()) >= 5;
    }

    nextYear() {
        this.year = this.year + 1;
        this.months.set(this.getMonthsMatrix());
        this.disableNextYear = this.year - getYear(new Date()) >= 5;
    }

    getStartDate(month: string) {
        return new Date(this.year, Number(month.substring(0, 2)) - 1, 1);
    }

    getMaxDate(month: string) {
        return lastDayOfMonth(new Date(this.year, Number(month.substring(0, 2)) - 1, 1));
    }

    getMinDate(month: string) {
        return new Date(this.year, Number(month.substring(0, 2)) - 1, 1)
    }

    closeDialog() {
        this.dialogRef.close();
    }

    async accept() {
        const calendar: Partial<NextCalendar> = this.formGroup.getRawValue();
        if (!this.formGroup.get('keepSync').value) {
            delete calendar.syncOid;
        }
        try {
            const _calendar = await firstValueFrom(this.projectsService.flushCalendar({ calendarDates: this.daysSelected, calendar, locator: this.data.locator }));
            this.dialogRef.close(_calendar);
        } catch (error) {
            console.error(error);
            this.dialogsService.openErrorDialog({
                message: error?.error?.echo && Object.keys(error?.error?.echo).length > 0 ? this.localize.transform(error?.error?.echo) : undefined,
            });
        }
    }

    private getMonthsMatrix() {
        return [
            ['01' + this.year, '02' + this.year, '03' + this.year, '04' + this.year],
            ['05' + this.year, '06' + this.year, '07' + this.year, '08' + this.year],
            ['09' + this.year, '10' + this.year, '11' + this.year, '12' + this.year]
        ];
    }
}